import React, { useRef, forwardRef, useImperativeHandle } from "react";
import { message, Modal } from "antd";
import { useSelector } from 'react-redux';
import * as mammoth from 'mammoth';
import { $generateNodesFromDOM } from '@lexical/html';
import { $getRoot } from 'lexical';
import { safeParseEditorState } from '../../utils/helper';
import { rewriteDataUrlImagesInDocument } from '../../utils/imageUpload';

const ImportMenu = forwardRef(({ setFileMenuVisible }, ref) => {
    const editor = useSelector((state) => state.editor.lexicalEditor);

    const closeFileMenu = () => {
        if (typeof setFileMenuVisible === 'function') {
            setFileMenuVisible(false);
        }
    };

    const handleFileChange = async (event) => {
        event.preventDefault();
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.docx', '.html', '.txt', '.pad'];
        const fileExtension = `.${file.name.split('.').pop()}`.toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            message.error('Invalid file type!');
            event.target.value = '';
            return;
        }

        closeFileMenu();

        const modal = Modal.confirm({
            title: "Confirm",
            content: "Do you want to overwrite?",
            okText: "Yes",
            cancelText: "No",
            onOk: async () => {
                let importSuccess = false;
                try {
                    switch (fileExtension) {
                        case '.pad':
                            await importLexicalFile(file);
                            message.success("Pad file imported successfully!");
                            importSuccess = true;
                            break;
                        case '.html':
                            await importHtmlFile(file);
                            message.success("HTML file imported successfully!");
                            importSuccess = true;
                            break;
                        case '.txt':
                            await importTextFile(file);
                            message.success("Text file imported successfully!");
                            importSuccess = true;
                            break;
                        case '.docx':
                            await importDocxFile(file);
                            message.success("DOCX file imported successfully!");
                            importSuccess = true;
                            break;
                        default:
                            console.error('Unsupported file type:', fileExtension);
                            message.error('Unsupported file type!');
                    }
                } catch (error) {
                    console.error(`Error importing ${fileExtension} file:`, error);
                    message.error(`An error occurred while importing the file. Please try again.`);
                } finally {
                    event.target.value = '';
                    modal.destroy();
                }
            },
            onCancel: () => {
                event.target.value = '';
                modal.destroy();
                closeFileMenu();
            },
        });
    };

    // Import .lexical file
    const importLexicalFile = async (file) => {
        const text = await file.text();
        // Do not parse the string here; pass as raw string to safeParseEditorState
        editor.update(() => {
            editor.setEditorState(safeParseEditorState(editor, text));
        });
    };

    const importHtmlFile = async (file) => {
        try {
            const text = await file.text();
            const parser = new DOMParser();
            const dom = parser.parseFromString(text, 'text/html');
            await rewriteDataUrlImagesInDocument(dom);
            editor.update(() => {
                const nodes = $generateNodesFromDOM(editor, dom);
                const root = $getRoot();
                root.clear();
                // Append onto root — $insertNodes uses selection/previousSelection and can append at the cursor instead of replacing.
                if (nodes.length > 0) {
                    root.append(...nodes);
                }
            });
        } catch (error) {
            console.error('Error importing HTML file:', error);
            throw error;
        }
    };

    // Import .txt file
    const importTextFile = async (file) => {
        const text = await file.text();
        editor.update(() => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(text, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.clear();
            if (nodes.length > 0) {
                root.append(...nodes);
            }
        });
    };

    // Import .docx file
    const importDocxFile = async (file) => {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        const htmlContent = result.value;

        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlContent, 'text/html');
        await rewriteDataUrlImagesInDocument(dom);

        editor.update(() => {
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.clear();
            // Append onto root — $insertNodes uses selection/previousSelection and can append at the cursor instead of replacing.
            if (nodes.length > 0) {
                root.append(...nodes);
            }
        });
    };

    const fileInputRef = useRef(null);

    useImperativeHandle(ref, () => ({
        openFileDialog: () => fileInputRef.current?.click(),
    }));

    const handleImportClick = (e) => {
        // Prevent the click event bubbling up to parent Menu (we close explicitly after opening the picker).
        e.stopPropagation();
        fileInputRef.current?.click();
        closeFileMenu();
    };

    return (
        <>
            <span
                onMouseDown={handleImportClick}
                onClick={handleImportClick}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleImportClick(e);
                    }
                }}
                role="button"
                tabIndex={0}
                className="toolbar-item spaced"
            >
                Import
            </span>
            <input
                ref={fileInputRef}
                type="file"
                accept=".docx,.html,.txt,.pad"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                data-testid="import-file-input"
            />
        </>
    );
});

export default ImportMenu;